import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { chipResourceKey } from './chipResourceKey';
import { useResolvedResourceContent } from './useResolvedResourceContent';

/**
 * **Ja oder Nein, bevor ein Werkzeug etwas anlegt oder ändert.**
 *
 * Hält der Chat bei einem schreibenden Werkzeug an, fragt tri-server mit Werkzeug und Werten nach
 * (`approvalRequest`). „Ja“ sendet die ursprüngliche Anfrage erneut, an genau dieses Werkzeug
 * geheftet (`exclusiveToolId`) — eine Auswahl gilt dort als Freigabe. „Nein“ führt nichts aus.
 * Bis zum 14.10.2026 stand an dieser Stelle der Hinweis, das Werkzeug im Eingabebereich zu wählen.
 */
interface ApprovalRequestContent {
    toolId: string;
    label: string;
    prompt: string;
    values?: string[];
}

interface ApprovalRequestResourceProps {
    resource: {
        type: 'approvalRequest';
        reference?: string;
        data?: ApprovalRequestContent;
        content?: ApprovalRequestContent;
    };
    onSubmitInput?: (input: string, prompt?: string, options?: { exclusiveToolId?: string; onAccepted?: () => void }) => void;
    isLastMessage?: boolean;
    stableKey?: string;
    pendingRequest?: boolean;
}

// Survives re-renders of the message list, like the other chips.
const decided = new Map<string, 'yes' | 'no'>();

export const ApprovalRequestResource: React.FC<ApprovalRequestResourceProps> = ({ resource, onSubmitInput, isLastMessage, stableKey, pendingRequest }) => {
    const { t } = useTranslation();
    const content = useResolvedResourceContent<ApprovalRequestContent>(resource);
    const key = chipResourceKey(stableKey, 'approvalRequest', [content?.toolId ?? '', content?.prompt ?? '']);
    const [decision, setDecision] = useState<'yes' | 'no' | undefined>(decided.get(key));

    if (!content?.toolId) return null;
    if (decision === 'no') {
        return <p className="mb-2 mt-3 text-xs text-muted-foreground">{t('chatBotPage.approval.declined', 'Nicht ausgeführt.')}</p>;
    }
    if (decision === 'yes' || !isLastMessage) return null;

    const approve = () => {
        if (pendingRequest || !onSubmitInput) return;
        onSubmitInput(t('chatBotPage.approval.yesBubble', 'Ja, {{label}}', { label: content.label }), content.prompt, {
            exclusiveToolId: content.toolId,
            onAccepted: () => {
                decided.set(key, 'yes');
                setDecision('yes');
            },
        });
    };
    const decline = () => {
        decided.set(key, 'no');
        setDecision('no');
    };

    return (
        <div className="mb-2 mt-3 flex flex-wrap items-center gap-2" data-testid="chat-resource-approval">
            <Button size="sm" onClick={approve} disabled={pendingRequest} data-testid="chat-approval-yes">
                <Check aria-hidden className="mr-1.5 h-3.5 w-3.5" />
                {t('chatBotPage.approval.yes', 'Ja, ausführen')}
            </Button>
            <Button size="sm" variant="outline" onClick={decline} disabled={pendingRequest} data-testid="chat-approval-no">
                <X aria-hidden className="mr-1.5 h-3.5 w-3.5" />
                {t('chatBotPage.approval.no', 'Nein')}
            </Button>
        </div>
    );
};
